import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { registerForPushNotifications } from '@/lib/fcm';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gm_user') || 'null'); } catch (e) {
      console.warn('AuthContext: failed to parse cached user', e);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('gm_token');
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      localStorage.setItem('gm_user', JSON.stringify(data));
    } catch (e) {
      console.warn('AuthContext: /auth/me failed', e?.response?.status || e?.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // After a user is authenticated, register the browser for FCM push ONLY if
  // permission was already granted earlier. Otherwise we wait — the
  // PushPrimerSheet shows a soft sheet first and triggers
  // registerForPushNotifications() when the user explicitly taps "Allow".
  // This raises opt-in rates dramatically vs immediately firing the native
  // dialog with zero context.
  useEffect(() => {
    if (!user?.id) return;
    // Skip on BecAIm Business routes — they have their own AuthProvider and
    // FCM tokens should be scoped to the consumer side only here.
    try {
      const path = typeof window !== 'undefined' ? (window.location.pathname || '') : '';
      if (path === '/' || path.startsWith('/business')) return;
    } catch { /* ignore */ }
    const flagKey = `gm_fcm_done_${user.id}`;
    if (sessionStorage.getItem(flagKey)) return;
    sessionStorage.setItem(flagKey, '1');
    try {
      // Web: silently register only if already granted. Otherwise the primer
      // sheet will handle it.
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return;
    } catch { /* SSR — fall through */ }
    registerForPushNotifications().catch(() => {});
  }, [user?.id]);

  const login = useCallback((token, u) => {
    localStorage.setItem('gm_token', token);
    localStorage.setItem('gm_user', JSON.stringify(u));
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('gm_token');
    localStorage.removeItem('gm_user');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, loading, refresh }),
    [user, login, logout, loading, refresh]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
